import { useState } from "react";
import { Link } from "react-router-dom";
import type { AttentionPoint, IntelSeverity } from "../../lib/intelligence/executive";
import { memoryPath } from "../../lib/intelligence/memory-path";
import { IconCheck, IconChevronDown, IconChevronRight } from "../icons";

const TONE: Record<IntelSeverity, { stripe: string; label: string; text: string }> = {
  critical: { stripe: "bg-danger-600", label: "Crítico", text: "text-danger-600" },
  warning:  { stripe: "bg-warning-500", label: "Atenção", text: "text-warning-700" },
  info:     { stripe: "bg-info-500", label: "Acompanhar", text: "text-info-600" },
};

/**
 * "O que pede ação": os pontos de atenção como uma fila, do mais urgente para o
 * menos urgente. Cada linha responde às 5 perguntas — o que aconteceu (fato),
 * qual o impacto, o que fazer, por que merece atenção (os números, ao abrir) e a
 * prioridade (a posição na fila) — e leva à memória de cálculo, quando existe.
 */
export function AttentionPanel({ points, loading, empty }: {
  points: AttentionPoint[];
  loading?: boolean;
  /** texto quando nada pede ação (padrão: o da Visão Executiva) */
  empty?: { title: string; text: string };
}) {
  return (
    <section id="o-que-pede-acao" aria-labelledby="o-que-pede-acao-titulo" className="scroll-mt-24">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h2 id="o-que-pede-acao-titulo" className="font-display font-semibold text-lg text-gray-900">
          O que pede ação
        </h2>
        {!loading && points.length > 0 && (
          <p className="text-xs text-gray-600">Do mais urgente para o menos urgente</p>
        )}
      </div>

      {loading ? (
        <div className="card px-4 py-3 text-sm text-gray-600">Calculando os pontos de atenção…</div>
      ) : points.length === 0 ? (
        <div className="card px-4 py-3 flex items-start gap-3">
          <IconCheck className="w-5 h-5 text-tm-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-gray-900">{empty?.title ?? "Nada pede ação neste período"}</p>
            <p className="text-sm text-gray-600 mt-0.5">
              {empty?.text ?? "Faturamento, volume de CT-es, ticket médio, inadimplência, saldo bancário e concentração de clientes estão dentro dos limites acompanhados."}
            </p>
          </div>
        </div>
      ) : (
        <ol className="card divide-y divide-surface-border overflow-hidden">
          {points.map((pt) => <QueueRow key={pt.id} point={pt} />)}
        </ol>
      )}
    </section>
  );
}

function QueueRow({ point: pt }: { point: AttentionPoint }) {
  const [open, setOpen] = useState(false);
  const tone = TONE[pt.severity];
  const detailsId = `ponto-${pt.id}-numeros`;

  return (
    <li id={`ponto-${pt.id}`} className="relative grid grid-cols-[1.75rem_1fr] sm:grid-cols-[2rem_1fr_minmax(0,15rem)] gap-x-3 gap-y-1 pl-4 pr-4 py-3 scroll-mt-24">
      <span className={`absolute left-0 inset-y-0 w-1 ${tone.stripe}`} aria-hidden="true" />

      {/* Prioridade = posição na fila */}
      <p className="font-display font-semibold text-2xl leading-none text-gray-300 tabular-nums" aria-label={`Prioridade ${pt.priority}`}>
        {pt.priority}
      </p>

      {/* O que aconteceu? / O que fazer? */}
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 leading-snug">
          <span className={`mr-2 text-xs font-semibold ${tone.text}`}>{tone.label}</span>
          {pt.title}
        </h3>
        <p className="text-sm text-gray-700 mt-0.5 leading-snug">{pt.fact}</p>
        <p className="text-sm text-gray-700 mt-1 leading-snug">
          <span className="font-semibold text-gray-900">O que fazer: </span>{pt.action}
        </p>

        {open && (
          <dl id={detailsId} className="mt-2 rounded-lg bg-surface-muted px-3 py-2 space-y-1">
            {pt.evidence.map((ev) => (
              <div key={ev.label} className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                <dt className="text-gray-600">{ev.label}{ev.note ? ` (${ev.note})` : ""}</dt>
                <dd className="font-semibold text-gray-900 tabular-nums">{ev.value}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={detailsId}
            className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500"
          >
            {open ? <IconChevronDown className="w-3.5 h-3.5" /> : <IconChevronRight className="w-3.5 h-3.5" />}
            {open ? "Ocultar os números" : "Ver os números"}
          </button>
          {pt.memory && (
            <Link
              to={memoryPath(pt.memory)}
              className="text-xs font-medium text-tm-700 hover:text-tm-800 underline-offset-2 hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500"
            >
              Ver como foi calculado
            </Link>
          )}
        </div>
      </div>

      {/* Qual é o impacto? */}
      <p className="col-start-2 sm:col-start-3 sm:row-start-1 text-sm leading-snug text-gray-900 sm:text-right">
        <span className="block text-xs text-gray-600">Impacto</span>
        <span className="font-semibold">{pt.impact}</span>
      </p>
    </li>
  );
}
